// Utility functions for campaign message personalization
// Replaces placeholders like {name}, {date}, {time}, etc. with actual values

import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Generate a referral code for a customer based on their ID
 * @param {string} customerId - The customer's ID
 * @returns {string} - Generated referral code
 */
export const generateReferralCode = (customerId) => {
  if (!customerId) return 'REFER2024';
  // Take first 4 chars of customer ID (uppercase) + random 4 digits
  const prefix = customerId.substring(0, 4).toUpperCase();
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}${suffix}`;
};

/**
 * Format a date from Firebase timestamp or Date object
 * @param {Object|Date|string} dateValue - The date to format
 * @returns {string} - Formatted date string (e.g., "January 25, 2024")
 */
export const formatDate = (dateValue) => {
  if (!dateValue) return '';

  let date;
  if (dateValue.toDate) {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a time from Firebase timestamp or Date object
 * @param {Object|Date|string} dateValue - The date/time to format
 * @returns {string} - Formatted time string (e.g., "2:30 PM")
 */
export const formatTime = (dateValue) => {
  if (!dateValue) return '';

  let date;
  if (dateValue.toDate) {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format currency amount
 * @param {number} amount - The amount to format
 * @returns {string} - Formatted currency string (e.g., "$150.00")
 */
export const formatAmount = (amount) => {
  if (amount === undefined || amount === null) return '$0.00';
  return `$${Number(amount).toFixed(2)}`;
};

/**
 * Get service name by ID
 * @param {string} serviceId - The service ID
 * @param {string} salonId - The salon ID
 * @returns {Promise<string>} - Service name
 */
export const getServiceName = async (serviceId, salonId) => {
  if (!serviceId) return 'your service';

  try {
    // Try flat services collection first
    const serviceDoc = await getDoc(doc(db, 'services', serviceId));
    if (serviceDoc.exists()) {
      return serviceDoc.data().name || 'your service';
    }

    // Fallback: search by salonId
    const servicesRef = collection(db, 'services');
    const snapshot = await getDocs(servicesRef);
    let serviceName = 'your service';

    snapshot.forEach((doc) => {
      if (doc.id === serviceId || doc.data().id === serviceId) {
        serviceName = doc.data().name || 'your service';
      }
    });

    return serviceName;
  } catch (error) {
    console.error('Error fetching service name:', error);
    return 'your service';
  }
};

/**
 * Get staff name by ID
 * @param {string} staffId - The staff ID
 * @param {string} salonId - The salon ID
 * @returns {Promise<string>} - Staff name
 */
export const getStaffName = async (staffId, salonId) => {
  if (!staffId) return 'our team';

  try {
    // Try flat staff collection first
    const staffDoc = await getDoc(doc(db, 'staff', staffId));
    if (staffDoc.exists()) {
      return staffDoc.data().name || 'our team';
    }

    // Fallback: search in collection
    const staffRef = collection(db, 'staff');
    const snapshot = await getDocs(staffRef);
    let staffName = 'our team';

    snapshot.forEach((doc) => {
      if (doc.id === staffId || doc.data().id === staffId) {
        staffName = doc.data().name || 'our team';
      }
    });

    return staffName;
  } catch (error) {
    console.error('Error fetching staff name:', error);
    return 'our stylist';
  }
};

/**
 * Get salon name from the clients collection
 * @param {string} salonId - The salon ID
 * @returns {Promise<string>} - Salon name
 */
export const getSalonName = async (salonId) => {
  if (!salonId) return '';
  try {
    const salonDoc = await getDoc(doc(db, 'salons', salonId));
    if (salonDoc.exists()) {
      const d = salonDoc.data();
      return d.branding?.name || d.name || '';
    }
    return '';
  } catch (error) {
    console.error('Error fetching salon name:', error);
    return '';
  }
};

/**
 * Get salon phone from config
 * @param {string} salonId - The salon ID
 * @returns {Promise<string>} - Salon phone number
 */
export const getSalonPhone = async (salonId) => {
  if (!salonId) return '';

  try {
    const salonDoc = await getDoc(doc(db, 'salons', salonId));
    if (salonDoc.exists()) {
      const data = salonDoc.data();
      return data.contact?.phone || data.phone || '';
    }
    return '';
  } catch (error) {
    console.error('Error fetching salon phone:', error);
    return '';
  }
};

/**
 * Get the most recent appointment for a customer
 * @param {string} customerId - The customer ID
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object|null>} - Appointment data or null
 */
export const getCustomerAppointment = async (customerId, salonId) => {
  if (!customerId || !salonId) return null;

  try {
    const appointmentsRef = collection(db, 'appointments');
    const snapshot = await getDocs(appointmentsRef);

    let latestAppointment = null;
    let latestDate = null;

    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.customerId === customerId && data.salonId === salonId) {
        const appointmentDate = data.dateTimeRequested?.toDate
          ? data.dateTimeRequested.toDate()
          : new Date(data.dateTimeRequested);

        if (!latestDate || appointmentDate > latestDate) {
          latestDate = appointmentDate;
          latestAppointment = { id: doc.id, ...data };
        }
      }
    });

    return latestAppointment;
  } catch (error) {
    console.error('Error fetching customer appointment:', error);
    return null;
  }
};

/**
 * Get the most recent transaction for a customer
 * @param {string} customerId - The customer ID
 * @param {string} salonId - The salon ID
 * @returns {Promise<Object|null>} - Transaction data or null
 */
export const getCustomerTransaction = async (customerId, salonId) => {
  if (!customerId || !salonId) return null;

  try {
    const transactionsRef = collection(db, 'transactions');
    const snapshot = await getDocs(transactionsRef);

    let latestTransaction = null;
    let latestDate = null;

    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.customerId === customerId && data.salonId === salonId) {
        const transactionDate = data.createdAt?.toDate
          ? data.createdAt.toDate()
          : (data.date?.toDate ? data.date.toDate() : new Date(data.createdAt || data.date));

        if (!latestDate || transactionDate > latestDate) {
          latestDate = transactionDate;
          latestTransaction = { id: doc.id, ...data };
        }
      }
    });

    return latestTransaction;
  } catch (error) {
    console.error('Error fetching customer transaction:', error);
    return null;
  }
};

/**
 * Calculate points to next reward (assuming standard tiers)
 * @param {number} currentPoints - Current points balance
 * @returns {number} - Points needed for next reward
 */
export const calculatePointsToReward = (currentPoints) => {
  // Standard reward thresholds
  const thresholds = [100, 250, 500, 1000, 2000, 5000];

  for (const threshold of thresholds) {
    if (currentPoints < threshold) {
      return threshold - currentPoints;
    }
  }

  // If above all thresholds, next milestone is next 1000
  const nextThousand = Math.ceil((currentPoints + 1) / 1000) * 1000;
  return nextThousand - currentPoints;
};

/**
 * Personalize a message by replacing placeholders with actual values
 * @param {string} message - The message template with placeholders
 * @param {Object} customer - Customer data
 * @param {Object} options - Additional options (salonId, appointment, transaction, salonPhone)
 * @returns {Promise<string>} - Personalized message
 */
export const personalizeMessage = async (message, customer, options = {}) => {
  if (!message) return '';

  const { salonId, appointment, transaction, salonPhone, salonName, services, staffList, promotional = false } = options;

  let personalizedMessage = message;

  // ── Salon-level tokens ──────────────────────────────────────────────────────
  // Resolve salon name: prefer the value passed in options, otherwise fetch it
  const resolvedSalonName = salonName || (salonId ? await getSalonName(salonId) : '');
  if (resolvedSalonName) {
    personalizedMessage = personalizedMessage.replace(/\{salonName\}/gi, resolvedSalonName);
  }

  // ── Customer-level tokens ───────────────────────────────────────────────────
  const customerDisplayName = customer?.displayName || customer?.name || 'Valued Customer';
  // {name} — original token used by system templates
  personalizedMessage = personalizedMessage.replace(/\{name\}/gi, customerDisplayName);
  // {customerName} — token used by Communications AI
  personalizedMessage = personalizedMessage.replace(/\{customerName\}/gi, customerDisplayName);

  // Loyalty tier — {loyaltyTier}
  // Field in Firestore customers collection is 'loyaltyTier' directly on the document
  const tier = customer?.loyaltyTier || customer?.tier || customer?.metrics?.tier || 'Valued';
  personalizedMessage = personalizedMessage.replace(/\{loyaltyTier\}/gi, tier);

  // Points balance — {pointsBalance}
  const totalPoints = customer?.metrics?.pointsBalance ?? customer?.pointsBalance ?? 0;
  personalizedMessage = personalizedMessage.replace(/\{pointsBalance\}/gi, totalPoints.toString());
  personalizedMessage = personalizedMessage.replace(/\{total_points\}/gi, totalPoints.toString());
  personalizedMessage = personalizedMessage.replace(/\{points_to_reward\}/gi, calculatePointsToReward(totalPoints).toString());

  // Referral code
  personalizedMessage = personalizedMessage.replace(/\{referral_code\}/gi, generateReferralCode(customer?.id));

  // Appointment-related placeholders
  // Promotional campaigns are not about the customer's last booking, so booking/transaction tokens are left
  // unresolved (and flagged by findUnresolvedPlaceholders) instead of silently filled with unrelated data.
  if (appointment && !promotional) {
    const appointmentDate = appointment.dateTimeRequested;
    personalizedMessage = personalizedMessage.replace(/\{date\}/gi, formatDate(appointmentDate));
    personalizedMessage = personalizedMessage.replace(/\{time\}/gi, formatTime(appointmentDate));

    // Service name - check if we have services list cached, otherwise fetch
    let serviceName = 'your service';
    if (appointment.serviceId || appointment.service) {
      const serviceId = appointment.serviceId || appointment.service;
      if (services && services.length > 0) {
        const service = services.find(s => s.id === serviceId);
        serviceName = service?.name || serviceName;
      } else {
        serviceName = await getServiceName(serviceId, salonId);
      }
    }
    personalizedMessage = personalizedMessage.replace(/\{service\}/gi, serviceName);
    personalizedMessage = personalizedMessage.replace(/\{service_name\}/gi, serviceName);
    personalizedMessage = personalizedMessage.replace(/\{serviceName\}/gi, serviceName);
    personalizedMessage = personalizedMessage.replace(/\{appointmentDate\}/gi, formatDate(appointmentDate));

    // Staff name - check if we have staff list cached, otherwise fetch
    let staffName = 'our team';
    if (appointment.staffId || appointment.staff) {
      const staffId = appointment.staffId || appointment.staff;
      if (staffList && staffList.length > 0) {
        const staff = staffList.find(s => s.id === staffId);
        staffName = staff?.name || staffName;
      } else {
        staffName = await getStaffName(staffId, salonId);
      }
    }
    personalizedMessage = personalizedMessage.replace(/\{staff\}/gi, staffName);
  } else if (!promotional) {
    // If no appointment provided, use generic placeholders
    personalizedMessage = personalizedMessage.replace(/\{date\}/gi, '[appointment date]');
    personalizedMessage = personalizedMessage.replace(/\{time\}/gi, '[appointment time]');
    personalizedMessage = personalizedMessage.replace(/\{service\}/gi, 'your service');
    personalizedMessage = personalizedMessage.replace(/\{service_name\}/gi, 'your service');
    personalizedMessage = personalizedMessage.replace(/\{serviceName\}/gi, 'your service');
    personalizedMessage = personalizedMessage.replace(/\{appointmentDate\}/gi, '[appointment date]');
    personalizedMessage = personalizedMessage.replace(/\{staff\}/gi, 'our team');
  }

  // Transaction-related placeholders
  if (transaction && !promotional) {
    personalizedMessage = personalizedMessage.replace(/\{amount\}/gi, formatAmount(transaction.totalAmount));
    personalizedMessage = personalizedMessage.replace(/\{points_earned\}/gi, (transaction.pointsEarned || 0).toString());
    personalizedMessage = personalizedMessage.replace(/\{points\}/gi, (transaction.pointsEarned || 0).toString());
  } else if (!promotional) {
    personalizedMessage = personalizedMessage.replace(/\{amount\}/gi, '[amount]');
    personalizedMessage = personalizedMessage.replace(/\{points_earned\}/gi, '0');
    personalizedMessage = personalizedMessage.replace(/\{points\}/gi, '0');
  }

  // Salon phone
  if (salonPhone) {
    personalizedMessage = personalizedMessage.replace(/\{phone\}/gi, salonPhone);
  } else if (salonId) {
    const phone = await getSalonPhone(salonId);
    personalizedMessage = personalizedMessage.replace(/\{phone\}/gi, phone || '[salon phone]');
  } else {
    personalizedMessage = personalizedMessage.replace(/\{phone\}/gi, '[salon phone]');
  }

  return personalizedMessage;
};

const KNOWN_TOKENS = new Set([
  'salonname', 'name', 'customername', 'loyaltytier', 'pointsbalance', 'total_points', 'points_to_reward',
  'referral_code', 'phone', 'unsuburl', 'sign_in_link'
]);
const BOOKING_TOKENS = new Set([
  'date', 'time', 'service', 'service_name', 'servicename', 'appointmentdate', 'staff', 'amount',
  'points_earned', 'points', 'invoice_number'
]);

/**
 * Find placeholders an admin must still fill in before a campaign can go out: unknown {tokens}, booking-specific
 * tokens in promotional campaigns (they would resolve to each customer's last booking), and [bracketed notes].
 * @param {string} text - Subject or body template
 * @param {Object} options - { promotional }
 * @returns {string[]} - Offending placeholders (empty when the text is ready to send)
 */
export const findUnresolvedPlaceholders = (text, { promotional = false } = {}) => {
  if (!text) return [];
  // Ignore <style>/<head> blocks of AI-generated HTML, whose CSS braces are not placeholders
  const body = String(text).replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<!--[\s\S]*?-->/g, '');
  const found = new Set();
  (body.match(/\{[a-zA-Z_]+\}/g) || []).forEach((tok) => {
    const key = tok.slice(1, -1).toLowerCase();
    if (KNOWN_TOKENS.has(key)) return;
    if (BOOKING_TOKENS.has(key) && !promotional) return;
    found.add(tok);
  });
  (body.match(/\[[A-Za-z][^\[\]<>]{2,40}\]/g) || []).forEach((tok) => found.add(tok));
  return [...found];
};

/**
 * Batch personalize messages for multiple customers
 * @param {string} messageTemplate - The message template
 * @param {Array} customers - Array of customer objects
 * @param {Object} options - Additional options (salonId, fetchAppointments, fetchTransactions)
 * @returns {Promise<Array>} - Array of {customer, personalizedMessage} objects
 */
export const batchPersonalizeMessages = async (messageTemplate, customers, options = {}) => {
  const { salonId, fetchAppointments = false, fetchTransactions = false, services, staffList, salonPhone } = options;

  const results = [];

  // Pre-fetch salon phone if not provided
  let phone = salonPhone;
  if (!phone && salonId && messageTemplate.includes('{phone}')) {
    phone = await getSalonPhone(salonId);
  }

  for (const customer of customers) {
    let appointment = null;
    let transaction = null;

    // Fetch appointment if needed and template contains appointment placeholders
    if (fetchAppointments && (
      messageTemplate.includes('{date}') ||
      messageTemplate.includes('{time}') ||
      messageTemplate.includes('{service}') ||
      messageTemplate.includes('{staff}')
    )) {
      appointment = await getCustomerAppointment(customer.id, salonId);
    }

    // Fetch transaction if needed and template contains transaction placeholders
    if (fetchTransactions && (
      messageTemplate.includes('{amount}') ||
      messageTemplate.includes('{points_earned}') ||
      messageTemplate.includes('{points}')
    )) {
      transaction = await getCustomerTransaction(customer.id, salonId);
    }

    const personalizedMessage = await personalizeMessage(messageTemplate, customer, {
      salonId,
      appointment,
      transaction,
      salonPhone: phone,
      services,
      staffList
    });

    results.push({
      customer,
      personalizedMessage,
      appointment,
      transaction
    });
  }

  return results;
};

const campaignPersonalization = {
  personalizeMessage,
  findUnresolvedPlaceholders,
  batchPersonalizeMessages,
  generateReferralCode,
  formatDate,
  formatTime,
  formatAmount,
  getServiceName,
  getStaffName,
  getSalonName,
  getSalonPhone,
  getCustomerAppointment,
  getCustomerTransaction,
  calculatePointsToReward
};


export default campaignPersonalization;
